import moment from "moment-timezone";

import { Device } from "@/services/features/DeviceService";
import type { Customer, Store } from "@/services/features/StoreService";

import {
  DeviceHealth,
  DeviceKind,
  DeviceStatus,
  StoreHealth,
  StoreHealthCache,
} from "./commandCenter.types";

const kinds: DeviceKind[] = ["SHOPPER", "DOOR", "ASSIST", "ASSOCIATE", "SURFACE", "CENTRAL"];

export type MonitoringHealthStatus = "healthy" | "toWatch" | "unhealthy" | "neutral" | "error";
const blank = (): DeviceHealth => ({
  total: 0,
  online: 0,
  warning: 0,
  offline: 0,
  escalated: 0,
  lowBattery: 0,
});
const asKind = (type: string): DeviceKind | null => {
  const value = type.toUpperCase();
  return kinds.includes(value as DeviceKind) ? (value as DeviceKind) : null;
};
const ageMinutes = (value: string | undefined, timezone: string, now: moment.Moment) =>
  value ? now.diff(moment(value).tz(timezone), "minutes") : null;

export const isEligibleDevice = (
  device: Device,
  timezone: string,
  now = moment.tz(timezone),
): boolean => {
  if (device.ignored) return false;
  if (!device.enabled) return false;
  const createdAge = ageMinutes(device.createdAt, timezone, now);
  const updatedAge = ageMinutes(device.updatedAt, timezone, now);
  if (!device.updatedAt && (createdAge ?? 0) >= 43_200) return false;
  if (updatedAge !== null && updatedAge >= 43_200) return false;
  return !(asKind(device.type) === "ASSOCIATE" && (updatedAge ?? Infinity) >= 10_080);
};
export const getDeviceStatus = (
  device: Device,
  timezone: string,
  now = moment.tz(timezone),
): DeviceStatus => {
  const age = ageMinutes(device.updatedAt, timezone, now);
  if (age === null || age > 15)
    return age !== null && age >= 10_080 && asKind(device.type) !== "ASSOCIATE"
      ? "escalated"
      : "offline";
  return age <= 5 ? "online" : "warning";
};
export const getStoreHealth = (
  devices: Device[],
  timezone: string,
  now = moment.tz(timezone),
): StoreHealth => {
  const byType = Object.fromEntries(kinds.map((kind) => [kind, blank()])) as StoreHealth["byType"];
  const total = blank();
  let hasRecentAssociateActivity = false;
  devices
    .filter((device) => isEligibleDevice(device, timezone, now))
    .forEach((device) => {
      const kind = asKind(device.type);
      if (!kind) return;
      const status = getDeviceStatus(device, timezone, now);
      [byType[kind], total].forEach((health) => {
        health.total += 1;
        health[status] += 1;
        if ((device.batteryPercent ?? 100) < 20) health.lowBattery += 1;
      });
      if (kind === "ASSOCIATE" && (ageMinutes(device.updatedAt, timezone, now) ?? Infinity) < 60)
        hasRecentAssociateActivity = true;
    });
  return { byType, total, hasRecentAssociateActivity };
};

export const getMonitoringHealthStatus = (health?: StoreHealth): MonitoringHealthStatus => {
  if (!health) return "neutral";

  const associate = health.byType.ASSOCIATE;
  const monitoredDeviceCount = health.total.total - associate.total;
  if (monitoredDeviceCount === 0) return "neutral";

  const offlineDeviceCount =
    health.total.offline + health.total.escalated - associate.offline - associate.escalated;
  if (offlineDeviceCount / monitoredDeviceCount >= 0.5) return "unhealthy";
  if (offlineDeviceCount > 1) return "toWatch";

  return "healthy";
};

export const getCustomerMonitoringHealthStatus = (
  storeHealths: StoreHealth[],
): Exclude<MonitoringHealthStatus, "neutral"> => {
  let hasStoreToWatch = false;

  for (const storeHealth of storeHealths) {
    const status = getMonitoringHealthStatus(storeHealth);
    if (status === "unhealthy") return "unhealthy";
    if (status === "toWatch") hasStoreToWatch = true;
  }

  return hasStoreToWatch ? "toWatch" : "healthy";
};

export type DeviceCount = Pick<
  DeviceHealth,
  "total" | "online" | "warning" | "offline" | "lowBattery"
>;
export type CustomerHealthSummary = Omit<DeviceCount, "total"> & { issueCount: number };
export type AssociateActivity = "active" | "noRecent" | "none";
export type CustomerDeviceTableRow = {
  customerRecord: Customer;
  customerId: number;
  customer: string;
  storeCount: number;
  total: DeviceCount;
  shopper: DeviceCount;
  door: DeviceCount;
  assist: DeviceCount;
  associate: DeviceCount;
  associateActivity: AssociateActivity;
  health: CustomerHealthSummary;
  healthStatus: Exclude<MonitoringHealthStatus, "neutral">;
  issueRank: number;
  loading: boolean;
};

const emptyCount = (): DeviceCount => ({
  total: 0,
  online: 0,
  warning: 0,
  offline: 0,
  lowBattery: 0,
});
const addCount = (target: DeviceCount, health: DeviceHealth) => {
  target.total += health.total;
  target.online += health.online;
  target.warning += health.warning;
  target.offline += health.offline + health.escalated;
  target.lowBattery += health.lowBattery;
};

/** Aggregate once per update, shared by the overview and customer table. */
export const getCustomerHealthRows = (
  customers: Customer[],
  stores: Store[],
  healthByStoreId: Record<number, StoreHealthCache>,
): CustomerDeviceTableRow[] => {
  const rows = customers.map((customer): CustomerDeviceTableRow => ({
    customerRecord: customer,
    customerId: customer.id,
    customer: customer.name,
    storeCount: 0,
    total: emptyCount(),
    shopper: emptyCount(),
    door: emptyCount(),
    assist: emptyCount(),
    associate: emptyCount(),
    associateActivity: "none",
    health: { online: 0, warning: 0, offline: 0, lowBattery: 0, issueCount: 0 },
    healthStatus: "healthy",
    issueRank: 1,
    loading: false,
  }));
  const byCustomer = new Map(rows.map((row) => [row.customerId, row]));
  for (const store of stores) {
    const row = byCustomer.get(store.customerId);
    if (!row) continue;
    row.storeCount += 1;
    const cache = healthByStoreId[store.id];
    row.loading ||= !cache || cache.state === "queued" || cache.state === "loading";
    const status = cache?.state === "error" ? "error" : getMonitoringHealthStatus(cache?.health);
    if (
      status === "error" ||
      (row.healthStatus !== "error" && status === "unhealthy") ||
      (row.healthStatus === "healthy" && status === "toWatch")
    )
      row.healthStatus = status;
    const health = cache?.health;
    if (!health) continue;
    addCount(row.total, health.total);
    addCount(row.shopper, health.byType.SHOPPER);
    addCount(row.door, health.byType.DOOR);
    addCount(row.associate, health.byType.ASSOCIATE);
    for (const kind of ["ASSIST", "SURFACE", "CENTRAL"] as const)
      addCount(row.assist, health.byType[kind]);
    row.health.online += health.total.online;
    row.health.warning += health.total.warning - health.byType.ASSOCIATE.warning;
    row.health.offline +=
      health.total.offline +
      health.total.escalated -
      health.byType.ASSOCIATE.offline -
      health.byType.ASSOCIATE.escalated;
    row.health.lowBattery += health.total.lowBattery;
    if (health.byType.ASSOCIATE.total > 0 && row.associateActivity === "none")
      row.associateActivity = "noRecent";
    if (health.hasRecentAssociateActivity) row.associateActivity = "active";
  }
  for (const row of rows) {
    row.health.issueCount = row.health.warning + row.health.offline + row.health.lowBattery;
    row.issueRank = row.health.issueCount > 0 ? 0 : 1;
  }
  const rank = { error: 0, unhealthy: 1, toWatch: 2, healthy: 3 };
  return rows.sort(
    (a, b) =>
      rank[a.healthStatus] - rank[b.healthStatus] ||
      Number(b.health.warning > 0 || b.health.offline > 0) -
        Number(a.health.warning > 0 || a.health.offline > 0) ||
      b.storeCount - a.storeCount ||
      a.customer.localeCompare(b.customer),
  );
};

/** Compare displayed values so an unrelated store update does not redraw a customer. */
export const equalCustomerHealthRows = (a: CustomerDeviceTableRow, b: CustomerDeviceTableRow) =>
  a.customerRecord === b.customerRecord &&
  a.storeCount === b.storeCount &&
  a.loading === b.loading &&
  a.healthStatus === b.healthStatus &&
  a.associateActivity === b.associateActivity &&
  (["total", "shopper", "door", "assist", "associate"] as const).every((kind) =>
    (Object.keys(a[kind]) as (keyof DeviceCount)[]).every(
      (metric) => a[kind][metric] === b[kind][metric],
    ),
  ) &&
  (Object.keys(a.health) as (keyof CustomerHealthSummary)[]).every(
    (metric) => a.health[metric] === b.health[metric],
  );
